import { format } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import useApiCall from '../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../hooks/useQuery';
import ConsultsService from '../../../services/ConsultsService';
import FilterRadioButton from '../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../FilterRadioButtonsContainer';
import FormGroup from '../../FormGroup';
import MyModal from '../../Modal';
import Textarea from '../../Textarea';
import { JustificativaDesarme, UsuImplantacoes } from '../types';

interface IImplantationModal {
  implantationModalShow: boolean;
  setImplantationModalShow: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  implantation: UsuImplantacoes;
  isImplant: boolean;
  getResult: () => Promise<void>;
  currentImplantationStatus: 'implant' | 'implantCanceled' | 'notImplant';
  removalImplantationHistory: JustificativaDesarme[];
}

const StyledUl = styled.ul`
  background: ${({ theme }) => theme.colors.backgroundWithOpacity};
`;

const StyledLi = styled.li`
  background: ${({ theme }) => theme.colors.backgroundWithOpacity};

  & + & {
    margin-top: 8px;
  }
`;

export default function ImplantationModal({ implantationModalShow, setImplantationModalShow, implantation, isImplant, getResult, setIsLoading, currentImplantationStatus, removalImplantationHistory }: IImplantationModal) {
  const [implantationStatus, setImplantationStatus] = useState<'implant' | 'implantCanceled' | 'notImplant'>(currentImplantationStatus);
  const [removingImplantationReason, setRemovingImplantationReason] = useState<string>('');

  const { consultCode } = useParams();
  const { apiCall } = useApiCall();
  const { userCode, userProfile, userName } = useGetGeneralAppData();
  const query = useQuery();
  const companyCode = query.get('codEmpresa');

  const changeConsultImplantation = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.changeConsultImplantation,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: Number(companyCode),
        codConsulta: Number(consultCode),
        implantar: implantationStatus !== 'notImplant',
        cancelarVT: implantationStatus === 'implantCanceled',
        justificativa: implantationStatus === 'notImplant' ? removingImplantationReason : '',
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível alterar a implantação da consulta. Por favor, tente novamente (${response.msg})`);
          return;
        }

        setImplantationModalShow(false);
        await getResult();
        toast.success('A implantação foi alterada com sucesso!');
      },
      catchMessage: 'Não foi possível alterar a implantação da consulta. Por favor, tente novamente',
    });
  }, [apiCall, companyCode, consultCode, getResult, implantationStatus, removingImplantationReason, setImplantationModalShow, setIsLoading, userCode, userName, userProfile]);

  const hasRemovalImplantationHistory = !!removalImplantationHistory && removalImplantationHistory?.length > 0;
  const lastImplantationRemoval = hasRemovalImplantationHistory ? removalImplantationHistory[0] : null;

  const explanationOfImplantationStatus: { [key: string]: string } = {
    implant: 'A rota exibida na página é a rota aceita e definida para a roteirização do funcionário sendo exibida.',
    implantCanceled: 'O funcionário não optará por usar o benefício do Vale Transporte.',
    notImplant: 'A roteirização do funcionário sendo exibida na página ainda não foi aceita ou definida, abrindo a possibilidade de realizar alterações até que a mesma seja adequada.',
  };

  return (
    <MyModal
      show={implantationModalShow}
      onClose={() => setImplantationModalShow(false)}
      title={'Alterar Implantação da Consulta'}
      // centeredBody
      modalBody={(
        <>
          {isImplant && (
            <h6 style={{ textAlign: 'center' }}>
              Implantada por {implantation.contatoNome || implantation.funcNome || implantation.usuCaptaNome} em {format(new Date(implantation.dataUltimaImplantada), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\'')}
            </h6>
          )}

          {hasRemovalImplantationHistory && !isImplant && (
            <h6 style={{ textAlign: 'center' }}>
            Última implantação removida por por {lastImplantationRemoval?.nomeUsuario} em {format(new Date(lastImplantationRemoval?.dataRegistro as string), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\'')} ({lastImplantationRemoval?.justificativa})
            </h6>
          )}

          <FilterRadioButtonsContainer style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <FilterRadioButton onClick={() => setImplantationStatus('implant')} selected={implantationStatus === 'implant'}>
              Implantada
            </FilterRadioButton>
            <FilterRadioButton onClick={() => setImplantationStatus('implantCanceled')} selected={implantationStatus === 'implantCanceled'}>
              Implantada (Com Cancelamento do VT)
            </FilterRadioButton>
            <FilterRadioButton onClick={() => setImplantationStatus('notImplant')} selected={implantationStatus === 'notImplant'}>
              Não Implantada
            </FilterRadioButton>
          </FilterRadioButtonsContainer>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <span>{explanationOfImplantationStatus[implantationStatus]}</span>
          </div>

          {(implantationStatus === 'notImplant' && currentImplantationStatus !== 'notImplant') && (
            <FormGroup>
              <label htmlFor="contactName">Justificativa do desarme</label>
              <Textarea
                placeholder="Porque a implantação está sendo removida?"
                value={removingImplantationReason}
                onChange={(e) => setRemovingImplantationReason(e.target.value)}
                darkBorder
                height={100}
              />
            </FormGroup>
          )}

          {hasRemovalImplantationHistory && (
            <div style={{ marginTop: '16px' }}>
              <h6>Histórico de desarmes</h6>
              <StyledUl style={{ height: '200px', overflow: 'auto' }}>
                {removalImplantationHistory.map((removal, index) => (
                  <StyledLi key={index}>
                    {removal.nomeUsuario} em {format(new Date(removal.dataRegistro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\'')} ({removal.justificativa})
                  </StyledLi>
                ))}
              </StyledUl>
            </div>
          )}
        </>
      )}
      closeButtonLabel="Fechar"
      size="lg"
      type='action'
      actionButtonLabel='Confirmar Alteração de Implantação'
      minHeight='500px'
      isActionButtonDisabled={(currentImplantationStatus === implantationStatus) || (implantationStatus === 'notImplant' && !removingImplantationReason)}
      onAction={() => changeConsultImplantation()}
    />
  );
}
